import React from "react";
import PropTypes from "prop-types";

import { getEltAndAccessorDate } from "./common/helpers";
import ResourcesTable, { caseInsensitiveFilter } from "./common/ResourcesTable";
import ClickableEntry from "./common/ClickableEntry";

/*
Constants and Helpers
*/

const COLUMN_STYLE = { whiteSpace: "unset", wordBreak: "break-word" };

// Given an array of strings, returns the cell accessor string
const getAccessorValue = (labels) =>
  labels.map((label) => label.toLowerCase()).join(" ");

// Given an array and onClickElt function, returns cell accessor and element,
// generated from the array elements if the 'name' parameter is falsy,
// or from the array elements' 'name' fields if 'name' is truthy.
// 'onClickElt' is attached as onClick to each entry that has 'id' field.
const getEltAndAccessor = ({ arr, labelKey, onClickElt = () => {} }) => {
  if (!arr) {
    return {
      accessor: "",
      element: null,
    };
  }

  // Generate the labels array
  let labels;
  if (!labelKey) {
    labels = arr.map((elt) => String(elt || ""));
  } else {
    labels = arr.map((elt) => String(elt[labelKey] || ""));
  }

  // Generate the accessor
  const accessor = getAccessorValue(labels);

  // Generate the element
  const element = (
    <div>
      {arr.map((elt, index) => {
        const { id: eltId } = elt;

        const label = labels[index];

        if (eltId === undefined) {
          return <div key={index}>{label}</div>;
        }

        return (
          <ClickableEntry
            clickable={!!eltId}
            key={index}
            label={label}
            onClickElt={onClickElt(eltId)}
          />
        );
      })}
    </div>
  );

  return {
    accessor,
    element,
  };
};

// Extracts the numeric id from a string of the form 'hostname/1234'
const extractHostId = (eltId) => {
  const re = /^(hostname\/)([\d]+)/;

  const result = eltId.match(re);
  const [, , hostId = ""] = result || {};

  return hostId;
};

// Given the Logdev element ID and its 'hostname' (as 'label'),
// returns the Hostname cell accessor and element.
// 'onClickElt' is attached as on click to the element.
const getEltAndAccessorHost = ({ eltId, label, onClickElt = () => {} }) => {
  if (!label) {
    return {
      accessor: "",
      element: null,
    };
  }

  // Get the numeric hostname ID from the Logdev ID for onClick
  const hostId = extractHostId(eltId);

  if (!hostId) {
    return {
      accessor: "",
      element: null,
    };
  }

  // Create the element
  const element = (
    <strong>
      <ClickableEntry
        label={label}
        onClickElt={onClickElt(hostId)}
        clickable={!!hostId}
      />
    </strong>
  );

  return {
    accessor: label,
    element,
  };
};

export const getRows = ({
  data,
  onClickHost = () => () => {},
  onClickIp = () => () => {},
  onClickSensor = () => () => {},
}) => {
  const rows = data.map((elt) => {
    const {
      firstParsed: firstSeen,
      groupedCount: total,
      hostname,
      id: eltId,
      ips,
      lastParsed: lastUpdated,
      logTypes,
      loggers: sensors,
    } = elt;

    // First Seen
    const {
      accessor: firstSeenAccessor,
      element: firstSeenElt,
      label: firstSeenLabel,
    } = getEltAndAccessorDate(firstSeen);

    // Hostname
    const { accessor: hostnameAccessor, element: hostElt } =
      getEltAndAccessorHost({
        eltId,
        label: hostname,
        onClickElt: onClickHost,
      });

    // IP Adresses
    const { accessor: ipsAccessor, element: ipsElt } = getEltAndAccessor({
      arr: ips,
      labelKey: "address",
      onClickElt: onClickIp,
    });

    // Last Updated
    const {
      accessor: lastUpdatedAccessor,
      element: lastUpdatedElt,
      label: lastUpdatedLabel,
    } = getEltAndAccessorDate(lastUpdated);

    // Log types
    const { accessor: logTypesAccessor, element: logTypesElt } =
      getEltAndAccessor({
        arr: logTypes,
      });

    // Sensors
    const { accessor: sensorsAccessor, element: sensorsElt } =
      getEltAndAccessor({
        arr: sensors,
        labelKey: "name",
        onClickElt: onClickSensor,
      });

    return {
      hostname: hostElt,
      hostnameAccessor,

      ips: ipsElt,
      ipsAccessor,

      sensors: sensorsElt,
      sensorsAccessor,

      logTypes: logTypesElt,
      logTypesAccessor,

      firstSeen: firstSeenElt,
      firstSeenAccessor,
      firstSeenLabel,

      lastUpdated: lastUpdatedElt,
      lastUpdatedAccessor,
      lastUpdatedLabel,

      total: <div>{total}</div>,
      totalAccessor: total,

      // Used to search the entire table
      accessorValueStrings: [
        firstSeenLabel,
        hostnameAccessor,
        ipsAccessor,
        lastUpdatedLabel,
        logTypesAccessor,
        sensorsAccessor,
        String(total),
      ],
    };
  });

  return rows;
};

const FilterComponent = ({ onChange }) => (
  <div>
    <input
      placeholder="Filter..."
      onChange={(event) => {
        onChange(event.target.value);
      }}
    />
  </div>
);

FilterComponent.propTypes = {
  onChange: PropTypes.func.isRequired,
};

const COLUMNS = [
  {
    Header: "Hostname",
    accessor: "hostnameAccessor",
    filterable: true,
    sortable: true,
    Cell: (row) => row.original.hostname,
    maxWidth: 220,
    minWidth: 180,
    Filter: ({ onChange }) => <FilterComponent onChange={onChange} />,
    filterMethod: caseInsensitiveFilter(),
  },
  {
    Header: "IP Addresses",
    accessor: "ipsAccessor",
    filterable: true,
    sortable: true,
    style: COLUMN_STYLE,
    Cell: (row) => row.original.ips,
    maxWidth: 180,
    minWidth: 140,
    filterMethod: caseInsensitiveFilter(),
  },
  {
    Header: "Sensors",
    accessor: "sensorsAccessor",
    filterable: true,
    sortable: true,
    style: COLUMN_STYLE,
    Cell: (row) => row.original.sensors,
    maxWidth: 220,
    minWidth: 180,
    filterMethod: caseInsensitiveFilter(),
  },
  {
    Header: "Log Types",
    accessor: "logTypesAccessor",
    filterable: true,
    sortable: true,
    style: COLUMN_STYLE,
    Cell: (row) => row.original.logTypes,
    maxWidth: 200,
    minWidth: 160,
    filterMethod: caseInsensitiveFilter(),
  },
  {
    Header: "First Seen",
    accessor: "firstSeenAccessor",
    filterable: true,
    sortable: true,
    style: COLUMN_STYLE,
    Cell: (row) => row.original.firstSeen,
    maxWidth: 180,
    minWidth: 140,
    filterMethod: caseInsensitiveFilter("firstSeenLabel"),
  },
  {
    Header: "Last Updated",
    accessor: "lastUpdatedAccessor",
    filterable: true,
    sortable: true,
    style: COLUMN_STYLE,
    Cell: (row) => row.original.lastUpdated,
    maxWidth: 180,
    minWidth: 140,
    filterMethod: caseInsensitiveFilter("lastUpdatedLabel"),
  },
  {
    Header: "Total",
    accessor: "totalAccessor",
    filterable: true,
    sortable: true,
    style: COLUMN_STYLE,
    maxWidth: 180,
    minWidth: 140,
    Cell: (row) => row.original.total,
  },
];

/*
Component
*/

const LogdevTable = (props) => {
  const { windowEnd, windowStart, rows } = props;

  return (
    <ResourcesTable
      data={rows}
      columns={COLUMNS}
      windowEnd={windowEnd}
      windowStart={windowStart}
    />
  );
};

LogdevTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({})),
  dataWindow: PropTypes.shape({}),
  windowEnd: PropTypes.string,
  windowStart: PropTypes.string,
};

LogdevTable.defaultProps = {
  rows: [],
  dataWindow: {},
  windowEnd: "",
  windowStart: "",
};

export default LogdevTable;
